import { useEffect, useRef, useState } from "react";

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let loading = null;

const loadScript = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => {
        loading = null;
        reject(new Error("Turnstile did not load"));
      };
      document.head.appendChild(script);
    });
  }
  return loading;
};

let warned = false;

const useTurnstile = (siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY) => {
  const ref = useRef(null);
  const widget = useRef(null);
  const retried = useRef(false);
  const [token, setToken] = useState("");
  const [failed, setFailed] = useState(false);
  // A production build without a site key would post an empty token and get a 403 on every visit.
  const misconfigured = !siteKey && process.env.NODE_ENV === "production";

  useEffect(() => {
    if (!misconfigured || warned) return;
    warned = true;
    console.error("REACT_APP_TURNSTILE_SITE_KEY is not set; the demand check cannot verify visitors.");
  }, [misconfigured]);

  useEffect(() => {
    if (!siteKey || !ref.current) return undefined;
    let cancelled = false;
    const fail = () => {
      if (!cancelled) setFailed(true);
    };
    const mount = (turnstile) => {
      if (cancelled || !ref.current || widget.current !== null) return;
      widget.current = turnstile.render(ref.current, {
        sitekey: siteKey,
        action: "demand-check",
        size: "flexible",
        appearance: "interaction-only",
        callback: (value) => {
          setFailed(false);
          setToken(value);
        },
        "expired-callback": () => setToken(""),
        "error-callback": () => {
          setToken("");
          if (!retried.current && window.turnstile && widget.current !== null) {
            retried.current = true;
            window.turnstile.reset(widget.current);
          } else {
            fail();
          }
        },
      });
    };
    loadScript()
      .then(mount)
      .catch(() => {
        retried.current = true;
        return loadScript().then(mount);
      })
      .catch(() => {
        setToken("");
        fail();
      });
    return () => {
      cancelled = true;
      if (widget.current !== null && window.turnstile) window.turnstile.remove(widget.current);
      widget.current = null;
    };
  }, [siteKey]);

  const reset = () => {
    setToken("");
    if (widget.current !== null && window.turnstile) window.turnstile.reset(widget.current);
  };

  const error = misconfigured || failed;

  return { ref, token, reset, error, enabled: Boolean(siteKey), ready: !error && (!siteKey || Boolean(token)) };
};

export default useTurnstile;
